import { useHotStreak } from '../hooks/useHotStreak';
import { useState } from 'react';
import { ReactionBar } from '../components/ReactionBar';
import { ReactionToasts } from '../components/ReactionToasts';
import { SoundbiteSheet } from '../components/SoundbiteSheet';
import { HotStreakBetting } from '../components/hot-streak/HotStreakBetting';
import { HotStreakCardSubmit } from '../components/hot-streak/HotStreakCardSubmit';
import { HotStreakDoubleDown } from '../components/hot-streak/HotStreakDoubleDown';
import { HotStreakRace } from '../components/hot-streak/HotStreakRace';
import { HotStreakResults } from '../components/hot-streak/HotStreakResults';
import { HotStreakGameOver } from '../components/hot-streak/HotStreakGameOver';
import '../styles/hot-streak.css';

interface Props {
  roomCode: string;
  playerId: string | null;
  reactions: {
    toasts: any[];
    sendReaction: (reactionId: string, icon?: string, label?: string) => void;
    toggleSoundbite: (soundbiteId: string) => void;
    startRecording: () => void;
    stopRecording: () => void;
    isRecording: boolean;
    cooldown: boolean;
    soundbiteCooldown: boolean;
    playingSoundbite: string | null;
  };
}

export function HotStreakGameScreen({ roomCode, playerId, reactions }: Props) {
  const [soundbiteSheetOpen, setSoundbiteSheetOpen] = useState(false);
  const hs = useHotStreak(roomCode);

  if (!hs.hsState) {
    return (
      <div className="hs-loading">
        <div className="hs-loading-mascots">
          <span className="hs-loading-mascot" style={{ animationDelay: '0s' }}>🌭</span>
          <span className="hs-loading-mascot" style={{ animationDelay: '0.15s' }}>🐻</span>
          <span className="hs-loading-mascot" style={{ animationDelay: '0.3s' }}>🐟</span>
          <span className="hs-loading-mascot" style={{ animationDelay: '0.45s' }}>👑</span>
        </div>
        <h2 className="hs-loading-title">Mascots are warming up...</h2>
        <p className="hs-loading-subtitle">The race is about to begin!</p>
        <div className="hs-loading-steps">
          <span>🎫 Place your bets</span>
          <span>→</span>
          <span>🃏 Sneak a card</span>
          <span>→</span>
          <span>🏁 Watch the race!</span>
        </div>
      </div>
    );
  }

  const { hsState, privateState } = hs;
  const currentPlayer = hsState.players.find((p) => p.id === playerId);

  const renderPhase = () => {
    switch (hsState.phase) {
      case 'betting':
        return (
          <HotStreakBetting
            state={hsState}
            playerId={playerId}
            onDraftPick={hs.draftPick}
          />
        );

      case 'double-down':
        return (
          <HotStreakDoubleDown
            state={hsState}
            playerId={playerId}
            privateState={privateState}
            onDoubleDown={hs.doubleDown}
          />
        );

      case 'card-submission':
        return (
          <HotStreakCardSubmit
            state={hsState}
            playerId={playerId}
            privateState={privateState}
            onSubmitCard={hs.submitCard}
          />
        );

      case 'racing':
        return (
          <HotStreakRace
            state={hsState}
            playerId={playerId}
            lastCard={hs.lastRaceCard}
            lastFold={hs.lastFold}
          />
        );

      case 'race-results':
        return (
          <HotStreakResults
            state={hsState}
            playerId={playerId}
          />
        );

      case 'game-over':
        return (
          <HotStreakGameOver
            state={hsState}
            playerId={playerId}
          />
        );

      default:
        return <p>Loading...</p>;
    }
  };

  return (
    <div className="hs-game">
      <div className="hs-header">
        <div className="hs-header-left">
          <span className="hs-race-badge">Race {hsState.currentRace}/3</span>
          <span className="hs-room-code">{roomCode}</span>
        </div>
        <div className="hs-header-right">
          {currentPlayer && (
            <span className="hs-money">${currentPlayer.money}</span>
          )}
        </div>
      </div>

      <div className="hs-content">
        {renderPhase()}
      </div>

      <div className="hs-social">
        <ReactionBar
          onSendReaction={reactions.sendReaction}
          onStartRecording={reactions.startRecording}
          onStopRecording={reactions.stopRecording}
          isRecording={reactions.isRecording}
          cooldown={reactions.cooldown}
          playingSoundbite={reactions.playingSoundbite}
          onOpenSoundbiteSheet={() => setSoundbiteSheetOpen(true)}
        />
        <SoundbiteSheet
          open={soundbiteSheetOpen}
          onClose={() => setSoundbiteSheetOpen(false)}
          onToggleSoundbite={reactions.toggleSoundbite}
          soundbiteCooldown={reactions.soundbiteCooldown}
          playingSoundbite={reactions.playingSoundbite}
        />
        <ReactionToasts toasts={reactions.toasts} />
      </div>
    </div>
  );
}
